import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/lib/auth'
import { usePermission } from '@/lib/permissions'
import {
  apiClient,
  type EventsParams,
  type OrdersParams,
  type TransactionsParams,
  type UsersParams,
  type VendorsParams,
  type VendorEventsParams,
  type MenuItemsParams,
  type VouchersParams,
  type ContentPagesParams,
  type CreateContentPageData,
} from './client'
import type { Event, MenuItem, Voucher, LiveStatsResponse, VendorEvent } from './types'

// Query Keys
export const queryKeys = {
  events: (params?: EventsParams) => ['events', params] as const,
  event: (id: number) => ['event', id] as const,
  orders: (eventId: number, params?: Omit<OrdersParams, 'vendor_event__event'>) => ['orders', eventId, params] as const,
  order: (id: number) => ['order', id] as const,
  transactions: (eventId: number, params?: Omit<TransactionsParams, 'event'>) => ['transactions', eventId, params] as const,
  transaction: (id: number) => ['transaction', id] as const,
  users: (eventId: number, params?: UsersParams) => ['users', eventId, params] as const,
  user: (id: number) => ['user', id] as const,
  vendors: (params?: VendorsParams) => ['vendors', params] as const,
  vendor: (id: number) => ['vendor', id] as const,
  vendorEvents: (eventId: number) => ['vendorEvents', eventId] as const,
  vendorEvent: (id: number) => ['vendorEvent', id] as const,
  menuItems: (params?: MenuItemsParams) => ['menuItems', params] as const,
  menuItem: (id: number) => ['menuItem', id] as const,
  vouchers: (params?: VouchersParams) => ['vouchers', params] as const,
  voucher: (id: number) => ['voucher', id] as const,
  voucherCounts: (eventId: number) => ['voucherCounts', eventId] as const,
  contentPages: (eventId: number, params?: ContentPagesParams) => ['contentPages', eventId, params] as const,
  contentPage: (id: number) => ['contentPage', id] as const,
  dashboardStats: (eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) => ['dashboardStats', eventId, params] as const,
  reporting: (eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) => ['reporting', eventId, params] as const,
  financials: (eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) => ['financials', eventId, params] as const,
  supportReloads: (eventId: number, params?: { created_at__gte?: string; created_at__lte?: string; staff?: string }) => ['supportReloads', eventId, params] as const,
  organizations: () => ['organizations'] as const,
  // Site Map
  mapConfig: (eventId: number) => ['mapConfig', eventId] as const,
  mapHeatmap: (eventId: number, params?: Record<string, unknown>) => ['mapHeatmap', eventId, params] as const,
  mapTimeline: (eventId: number, params?: Record<string, unknown>) => ['mapTimeline', eventId, params] as const,
  // Live Stats
  liveStats: (eventId: number, activeWindow?: number) => ['liveStats', eventId, activeWindow] as const,
  // Home Layout
  eventHomePages: (eventId: number) => ['eventHomePages', eventId] as const,
  eventHomePage: (id: number) => ['eventHomePage', id] as const,
  eventNavigations: (eventId: number) => ['eventNavigations', eventId] as const,
  eventCategories: (eventId: number) => ['eventCategories', eventId] as const,
  eventNavigation: (id: number) => ['eventNavigation', id] as const,
  eventHomePageNavigations: (homePageId: number) => ['eventHomePageNavigations', homePageId] as const,
  // Reviews
  orderReviews: (params?: Record<string, unknown>) => ['orderReviews', params] as const,
  orderReview: (id: number) => ['orderReview', id] as const,
  orderReviewSummary: (params?: Record<string, unknown>) => ['orderReviewSummary', params] as const,
  itemReviews: (params?: Record<string, unknown>) => ['itemReviews', params] as const,
  itemReview: (id: number) => ['itemReview', id] as const,
  itemReviewSummary: (params?: Record<string, unknown>) => ['itemReviewSummary', params] as const,
  // Support chat history (current user's past sessions, scoped to one event)
  supportChatSessions: (eventId: number) => ['supportChatSessions', eventId] as const,
}

// Events
export function useEvents(params?: EventsParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.events(params),
    queryFn: () => apiClient.getEvents(token!, params),
    enabled: !!token,
  })
}

export function useEvent(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.event(eventId),
    queryFn: () => apiClient.getEvent(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useUpdateEvent() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ eventId, data, files }: { eventId: number; data: Record<string, unknown>; files?: { banner?: File; logo?: File } }) => {
      if (files?.banner || files?.logo) {
        // Multipart cannot express null, and updateEventWithFiles drops null
        // entries — clears (signup_end_date_time: null, blanked location
        // fields) were silently lost on branding saves. Fields therefore
        // always travel via JSON PATCH (nulls included); the multipart
        // request carries only the files. Fields-first on purpose: if the
        // upload fails, the settings still saved and the user retries just
        // the image (onSettled below reconciles the cache either way).
        await apiClient.updateEvent(token!, eventId, data as Partial<Event>)
        return apiClient.updateEventWithFiles(token!, eventId, {}, files)
      }
      return apiClient.updateEvent(token!, eventId, data as Partial<Event>)
    },
    onSettled: (_updatedEvent, _error, variables) => {
      // Runs on failure too: a split save can partially persist (fields
      // saved, upload failed) — the UI must refetch what actually landed
      // rather than showing stale values next to the error.
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.event(variables.eventId) })
    },
  })
}

// Orders - now requires eventId
export function useOrders(eventId: number, params?: Omit<OrdersParams, 'vendor_event__event'>, options?: { refetchInterval?: number | false }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.orders(eventId, params),
    queryFn: () => apiClient.getOrders(token!, eventId, params),
    enabled: !!token && !!eventId,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: options?.refetchInterval ? 0 : undefined, // Always consider stale when auto-refresh is on
  })
}

export function useOrder(orderId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.order(orderId),
    queryFn: () => apiClient.getOrder(token!, orderId),
    enabled: !!token && !!orderId,
  })
}

// Transactions - now requires eventId
export function useTransactions(eventId: number, params?: Omit<TransactionsParams, 'event'>, options?: { refetchInterval?: number | false }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.transactions(eventId, params),
    queryFn: () => apiClient.getTransactions(token!, eventId, params),
    enabled: !!token && !!eventId,
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: false, // Don't refetch when tab is not visible
    staleTime: options?.refetchInterval ? 0 : undefined, // Always consider stale when auto-refresh is on
  })
}

export function useTransaction(transactionId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.transaction(transactionId),
    queryFn: () => apiClient.getTransaction(token!, transactionId),
    enabled: !!token && !!transactionId,
  })
}

// Users - uses /event/{eventId}/users/ endpoint
export function useEventUsers(eventId: number, params?: UsersParams, options?: { enabled?: boolean }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.users(eventId, params),
    queryFn: () => apiClient.getEventUsers(token!, eventId, params),
    enabled: !!token && !!eventId && (options?.enabled !== false),
  })
}

export function useUser(userId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.user(userId),
    queryFn: () => apiClient.getUser(token!, userId),
    enabled: !!token && !!userId,
  })
}

export function useCreateUser() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      event: number
      first_name: string
      last_name: string
      email?: string
      phone_number?: string
      password?: string
      confirm_password?: string
    }) => apiClient.createUser(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventUsers'] })
    },
  })
}

export function useUpdateUser() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: { first_name?: string; last_name?: string; email?: string } }) =>
      apiClient.updateUser(token!, userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// Vendors
export function useVendors(params?: VendorsParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.vendors(params),
    queryFn: () => apiClient.getVendors(token!, params),
    enabled: !!token,
  })
}

export function useVendor(
  vendorId: number,
  params?: { expand?: string; aggregates?: boolean; all_items?: boolean; event?: number; created_at__gte?: string; created_at__lte?: string },
  options?: { enabled?: boolean },
) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.vendor(vendorId), params],
    queryFn: () => apiClient.getVendor(token!, vendorId, params),
    enabled: !!token && !!vendorId && (options?.enabled ?? true),
  })
}

export function useUpdateVendor() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (args: { vendorId: number; data: { name?: string; description?: string; website?: string; phone?: string; is_approved?: boolean; logo?: File | null; background?: File | null } }) =>
      apiClient.updateVendor(token!, args.vendorId, args.data),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor(args.vendorId) })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}

export function useCreateVendor() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { 
      name: string
      description?: string
      website?: string
      phone?: string
      event?: number
      is_approved?: boolean
      logo?: File
      background?: File
    }) => apiClient.createVendor(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}

export function useVendorStats(vendorId: number, eventId: number, params?: { start?: string; end?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorStats', vendorId, eventId, params],
    queryFn: () => apiClient.getVendorStats(token!, vendorId, eventId, params),
    enabled: !!token && !!vendorId && !!eventId,
  })
}

export function useVendorCountStats(vendorId: number, eventId: number, params?: { top_count?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorCountStats', vendorId, eventId, params],
    queryFn: () => apiClient.getVendorCountStats(token!, vendorId, eventId, params),
    enabled: !!token && !!vendorId && !!eventId,
  })
}

export function useVendorLocations(vendorId: number, eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorLocations', vendorId, eventId],
    queryFn: () => apiClient.getVendorLocations(token!, vendorId, eventId),
    enabled: !!token && !!vendorId && !!eventId,
  })
}

export function useBulkImportVendors() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ eventId, file }: { eventId: number; file: File }) => 
      apiClient.bulkImportVendors(token!, eventId, file),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.vendorEvents(eventId) })
    },
  })
}

// Vendor Events (Locations)
export function useVendorEvents(
  eventId: number,
  params?: VendorEventsParams,
  options?: { enabled?: boolean; retryForbidden?: boolean; gcTime?: number },
) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.vendorEvents(eventId), params],
    // Callers that treat a 403 as an answer (no vendor access) skip its
    // retry and its error report.
    queryFn: () => apiClient.getVendorEvents(
      token!, eventId, params,
      options?.retryForbidden === false ? { expectedErrorStatuses: [403] } : undefined,
    ),
    enabled: !!token && !!eventId && options?.enabled !== false,
    ...(options?.gcTime !== undefined ? { gcTime: options.gcTime } : {}),
    ...(options?.retryForbidden === false
      ? { retry: (failures: number, error: unknown) => !isForbidden(error) && failures < 1 }
      : {}),
  })
}

export function useVendorEvent(vendorEventId: number, params?: { expand?: string; aggregates?: boolean; event?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.vendorEvent(vendorEventId), params],
    queryFn: () => apiClient.getVendorEvent(token!, vendorEventId, params),
    enabled: !!token && !!vendorEventId,
  })
}

export function useVendorEventCountStats(vendorEventId: number, params?: { top_count?: number; event?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorEventCountStats', vendorEventId, params],
    queryFn: () => apiClient.getVendorEventCountStats(token!, vendorEventId, params),
    enabled: !!token && !!vendorEventId,
  })
}

export function useLocationStats(
  vendorEventId: number,
  params?: { start?: string; end?: string; all_items?: boolean; created_at__gte?: string; created_at__lte?: string },
  options?: { enabled?: boolean },
) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['locationStats', vendorEventId, params],
    queryFn: () => apiClient.getLocationStats(token!, vendorEventId, params),
    enabled: !!token && !!vendorEventId && (options?.enabled ?? true),
  })
}

// Locations (vendor events) are rendered from more caches than the two obvious
// keys: the vendor-detail Locations table reads ['vendorLocations'], and both
// the vendors list and vendor detail render locations nested under
// ['vendors']/['vendor'] via the vendorevent_set expansion. With the global
// 5-minute staleTime, any key left out here keeps showing a deleted or
// out-of-date location until it expires — which is how an edit can be opened
// on a location that no longer exists and PATCH it into a 404.
export function invalidateLocationCaches(
  queryClient: QueryClient,
  args: { vendorEventId?: number; eventId?: number },
) {
  if (args.vendorEventId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.vendorEvent(args.vendorEventId) })
  }
  if (args.eventId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.vendorEvents(args.eventId) })
  }
  queryClient.invalidateQueries({ queryKey: ['vendorLocations'] })
  queryClient.invalidateQueries({ queryKey: ['vendors'] })
  queryClient.invalidateQueries({ queryKey: ['vendor'] })
}

export function useCreateVendorEvent() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: { data: Partial<VendorEvent>; eventId?: number }) =>
      apiClient.createVendorEvent(token!, args.data),
    onSuccess: (_, args) => {
      invalidateLocationCaches(queryClient, { eventId: args.eventId })
    },
  })
}

export function useUpdateVendorEvent() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: {
      vendorEventId: number
      data: { booth?: string; description?: string; is_active?: boolean; is_approved?: boolean; is_hidden?: boolean; categories?: number[]; card_payments?: boolean | null; card_fee_percentage?: number | null; card_fee_fixed?: number | null; logo?: File | null; background?: File | null }
      eventId?: number
    }) => {
      const { logo, background, categories, ...rest } = args.data
      if (logo !== undefined || background !== undefined) {
        const result = await apiClient.updateVendorEventWithFiles(token!, args.vendorEventId, rest, { logo, background })
        // M2M clears don't survive multipart (absent key = unchanged) — send as JSON
        if (categories !== undefined) {
          return apiClient.updateVendorEvent(token!, args.vendorEventId, { categories })
        }
        return result
      }
      return apiClient.updateVendorEvent(
        token!,
        args.vendorEventId,
        categories !== undefined ? { ...rest, categories } : rest,
      )
    },
    onSuccess: (_, args) => {
      invalidateLocationCaches(queryClient, {
        vendorEventId: args.vendorEventId,
        eventId: args.eventId,
      })
    },
  })
}

export function useDeleteVendorEvent() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: { vendorEventId: number; eventId?: number }) =>
      apiClient.deleteVendorEvent(token!, args.vendorEventId),
    onSuccess: (_, args) => {
      // The location is gone: evict its caches rather than invalidating them.
      // invalidateQueries keeps the old data and refetches, so a mounted
      // detail view would keep rendering a deleted location while its refetch
      // 404s. removeQueries drops it outright.
      queryClient.removeQueries({ queryKey: queryKeys.vendorEvent(args.vendorEventId) })
      queryClient.removeQueries({ queryKey: ['locationStats', args.vendorEventId] })
      queryClient.removeQueries({ queryKey: ['vendorEventAdministrators', args.vendorEventId] })
      invalidateLocationCaches(queryClient, { eventId: args.eventId })
    },
  })
}

// Vendor Administrators
export function useVendorAdministrators(vendorId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorAdministrators', vendorId],
    queryFn: () => apiClient.getVendorAdministrators(token!, vendorId),
    enabled: !!token && !!vendorId,
  })
}

export function useCreateVendorAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { 
      email: string
      vendor: number
      groups: number[]
      add_to_past_events?: boolean
      vendor_event_ids?: number[]
      event_id?: number
    }) => apiClient.createVendorAdministrator(token!, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vendorAdministrators', variables.vendor] })
      // Use partial match to invalidate vendor queries regardless of params
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor(variables.vendor) })
    },
  })
}

export function useVendorEventsByVendor(vendorId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorEventsByVendor', vendorId],
    queryFn: () => apiClient.getVendorEventsByVendor(token!, vendorId),
    enabled: !!token && !!vendorId,
  })
}

export function useUpdateVendorAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: { adminId: number; data: { groups?: number[]; vendor_event_ids?: number[]; event_id?: number }; vendorId: number }) =>
      apiClient.updateVendorAdministrator(token!, args.adminId, args.data),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorAdministrators', args.vendorId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor(args.vendorId) })
    },
  })
}

export function useDeleteVendorAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: { adminId: number; vendorId: number; eventId?: number }) =>
      apiClient.deleteVendorAdministrator(token!, args.adminId, args.eventId),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorAdministrators', args.vendorId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.vendor(args.vendorId) })
    },
  })
}

// Vendor Event Administrators (Location Users)
export function useVendorEventAdministrators(vendorEventId: number, eventId?: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['vendorEventAdministrators', vendorEventId, eventId],
    queryFn: () => apiClient.getVendorEventAdministrators(token!, vendorEventId, eventId),
    enabled: !!token && !!vendorEventId,
  })
}

export function useCreateVendorEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { email: string; vendor_event: number; groups: number[] }) =>
      apiClient.createVendorEventAdministrator(token!, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vendorEventAdministrators', variables.vendor_event] })
    },
  })
}

export function useUpdateVendorEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (args: { adminId: number; data: { groups: number[] }; vendorEventId: number }) =>
      apiClient.updateVendorEventAdministrator(token!, args.adminId, args.data),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorEventAdministrators', args.vendorEventId] })
    },
  })
}

export function useDeleteVendorEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (args: { adminId: number; vendorEventId: number }) =>
      apiClient.deleteVendorEventAdministrator(token!, args.adminId),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['vendorEventAdministrators', args.vendorEventId] })
    },
  })
}

// Event Administrators
export function useEventAdministrators(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['eventAdministrators', eventId],
    queryFn: () => apiClient.getEventAdministrators(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

// Whether the current user is exempt from the event's vendor_can_modify_item_*
// flags. Mirrors the backend exemption (VendorEventItemViewSet
// _is_vendor_level_admin): superusers, EventAdministrators for this event, and
// OrganizationAdministrators for the event's organization are never gated,
// even when their global permission union carries is_vendor from a vendor
// role elsewhere (dual-role users). The probes only fire for vendor-flagged
// users — the only case where the answer matters — and fail closed: the hook
// returns false while loading or on error.
export function useVendorGatingExemption(event: Event | undefined) {
  const { token, user } = useAuth()
  const isVendorFlagged = usePermission('is_vendor')
  const organizationId = typeof event?.organization === 'object' ? event.organization?.id : event?.organization
  const probesEnabled = !!token && !!user?.id && isVendorFlagged && !user.is_superuser

  const eventAdmin = useQuery({
    queryKey: ['eventAdminMembership', event?.id, user?.id],
    queryFn: () => apiClient.getMyEventAdministratorRecords(token!, event!.id, user!.id),
    enabled: probesEnabled && !!event?.id,
    staleTime: 5 * 60 * 1000,
  })
  const orgAdmin = useQuery({
    queryKey: ['orgAdminMembership', organizationId, user?.id],
    queryFn: () => apiClient.getMyOrganizationAdministratorRecords(token!, organizationId!, user!.id),
    enabled: probesEnabled && !!organizationId,
    // Non-org-admins always 403 here; don't retry an expected rejection.
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  return (user?.is_superuser ?? false)
    || (eventAdmin.data?.count ?? 0) > 0
    || (orgAdmin.data?.count ?? 0) > 0
}

// Whether this user may add menu items at this event: can_create_menu, and for
// a vendor-level admin the event's vendor_can_create_items, which also covers
// deleting them (the backend refuses them otherwise).
export function useCanAddMenuItems(eventId: number) {
  const { data: event } = useEvent(eventId)
  const canCreateMenu = usePermission('can_create_menu')
  const isVendor = usePermission('is_vendor')
  const isExempt = useVendorGatingExemption(event)
  return canCreateMenu && (!isVendor || isExempt || (!!event && event.vendor_can_create_items !== false))
}

export function useCreateEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { email: string; event: number; groups: number[] }) =>
      apiClient.createEventAdministrator(token!, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eventAdministrators', variables.event] })
    },
  })
}

export function useUpdateEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (args: { adminId: number; eventId: number; data: { group_ids: number[] } }) =>
      apiClient.updateEventAdministrator(token!, args.adminId, args.data),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['eventAdministrators', args.eventId] })
    },
  })
}

export function useDeleteEventAdministrator() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (args: { adminId: number; eventId: number }) =>
      apiClient.deleteEventAdministrator(token!, args.adminId),
    onSuccess: (_, args) => {
      queryClient.invalidateQueries({ queryKey: ['eventAdministrators', args.eventId] })
    },
  })
}

// Permission Groups
export function usePermissionGroups() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['permissionGroups'],
    queryFn: () => apiClient.getPermissionGroups(token!),
    enabled: !!token,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  })
}

// Menu Items
export function useMenuItems(params?: MenuItemsParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.menuItems(params),
    queryFn: () => apiClient.getMenuItems(token!, params),
    enabled: !!token,
  })
}

export function useMenuItem(itemId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.menuItem(itemId),
    queryFn: () => apiClient.getMenuItem(token!, itemId),
    enabled: !!token && !!itemId,
  })
}

export function useCreateMenuItem() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: Partial<MenuItem>) => apiClient.createMenuItem(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] })
    },
  })
}

export function useUpdateMenuItem() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<MenuItem> }) =>
      apiClient.updateMenuItem(token!, id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItem(id) })
      // A price change moves what the item's promotions charge: its schedule
      // list and every event's promotion data (discountKeys in discount-queries.ts).
      queryClient.invalidateQueries({ queryKey: ['item-discounts', id] })
      queryClient.invalidateQueries({ queryKey: ['discounts'] })
    },
  })
}

export function useDeleteMenuItem() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: number) => apiClient.deleteMenuItem(token!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] })
    },
  })
}

// Vouchers
export function useVouchers(params?: VouchersParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.vouchers(params),
    queryFn: () => apiClient.getVouchers(token!, params),
    enabled: !!token,
  })
}

export function useVoucher(voucherId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.voucher(voucherId),
    queryFn: () => apiClient.getVoucher(token!, voucherId),
    enabled: !!token && !!voucherId,
  })
}

export function useCreateVoucher() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: Partial<Voucher>) => apiClient.createVoucher(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] })
    },
  })
}

export function useUpdateVoucher() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Voucher> }) =>
      apiClient.updateVoucher(token!, id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.voucher(id) })
    },
  })
}

export function useDeleteVoucher() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => apiClient.deleteVoucher(token!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] })
    },
  })
}

export function useAssignVoucher() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ voucherId, userId }: { voucherId: number; userId: number }) =>
      apiClient.assignVoucher(token!, voucherId, userId),
    onSuccess: (_, { voucherId }) => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.voucher(voucherId) })
    },
  })
}

export function useVoucherCounts(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.voucherCounts(eventId),
    queryFn: () => apiClient.getVoucherCounts(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useBulkImportVouchers() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ eventId, file }: { eventId: number; file: File }) => 
      apiClient.bulkImportVouchers(token!, eventId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] })
      queryClient.invalidateQueries({ queryKey: ['voucherCounts'] })
    },
  })
}

// Dashboard - uses /order/count/ endpoint
export function useDashboardStats(eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.dashboardStats(eventId, params),
    queryFn: () => apiClient.getDashboardStats(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

// Reporting - uses /event/{event_id}/reporting/ endpoint
export function useReporting(eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.reporting(eventId, params),
    queryFn: () => apiClient.getReporting(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

// Event Insights Stats - uses /event/{event_id}/stats/ endpoint
export function useEventInsightsStats(eventId: number, params?: { top_count?: number; start_date?: string; end_date?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['eventInsightsStats', eventId, params] as const,
    queryFn: () => apiClient.getEventInsightsStats(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

// Organizations
export function useOrganizations() {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.organizations(),
    queryFn: () => apiClient.getUserOrganizations(token!),
    enabled: !!token,
  })
}

// Single organization by id (readable by any authenticated user). Used by the
// Toolkit to resolve the org slug for the public group-page link.
export function useOrganizationDetail(orgId?: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => apiClient.getOrganization(token!, orgId!),
    enabled: !!token && !!orgId,
  })
}

// Trackable short link for one (event, source) surface. Create-or-get is
// idempotent, so it's safe as a queryFn. Returns null (→ callers fall back to
// the direct URL) when the backend doesn't have the endpoint yet.
export function useShortLink(eventId?: number, source?: string, destination?: string | null) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['shortLink', eventId, source, destination],
    queryFn: async () => {
      try {
        return await apiClient.createOrGetShortLink(token!, {
          event: eventId!,
          source: source!,
          destination: destination!,
        })
      } catch (e) {
        // 404 = backend predates the endpoint → legacy fallback (null).
        // Anything else is a REAL failure — surface it so the UI never
        // presents an untracked QR as tracked.
        if ((e as { status?: number })?.status === 404) return null
        throw e
      }
    },
    enabled: !!token && !!eventId && !!source && !!destination,
    retry: 1,
    staleTime: 60_000,
  })
}

// All short links for an event (per-surface scan counts). Empty array when
// the backend doesn't have the endpoint yet.
export function useShortLinksList(eventId?: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['shortLinks', eventId],
    queryFn: async () => {
      try {
        return await apiClient.listShortLinks(token!, eventId!)
      } catch (e) {
        // 404 = backend predates the endpoint → quiet legacy fallback.
        // Real failures surface so the UI can say "couldn't load" instead of
        // silently rendering "no scans".
        if ((e as { status?: number })?.status === 404) return []
        throw e
      }
    },
    enabled: !!token && !!eventId,
    retry: 1,
    staleTime: 30_000,
  })
}

// Mutations for reload/transfer
export function useEventTerminals(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['event-terminals', eventId],
    queryFn: () => apiClient.getEventTerminals(token!, eventId),
    enabled: !!token && !!eventId,
    // As before for other errors (the app default: one retry); a 403 is an
    // access answer, not retried.
    retry: (failures, error) => !isForbidden(error) && failures < 1,
    staleTime: 60_000,
  })
}

/** The backend refused this login outright (not a blip). */
export function isForbidden(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 403
}

export const posGrantsKey = (eventId: number) => ['pos-grants', eventId] as const

/**
 * The event's card phones and trusted booths, for staff with can_add_terminal
 * at this event. A 403 is final: not retried, and polling stops (the sidebar
 * hides Card phones on it). Any other failure retries, so the page and its
 * nav entry survive a blip. `live` polls every 10 s, so a phone asking at a
 * booth shows up without a refresh.
 */
export function useEventPosGrants(eventId: number, options?: { enabled?: boolean; live?: boolean }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: posGrantsKey(eventId),
    queryFn: () => apiClient.getEventPosGrants(token!, eventId),
    enabled: !!token && !!eventId && options?.enabled !== false,
    retry: (failures, error) => !isForbidden(error) && failures < 2,
    staleTime: options?.live ? 0 : 60_000,
    refetchInterval: (query) => (options?.live && !isForbidden(query.state.error) ? 10_000 : false),
  })
}

// Not retried: a lost answer retried would find the phone already approved
// (or the booth already trusted) and report a failure.
function usePosGrantMutation<TArgs, TResult>(eventId: number, run: (token: string, args: TArgs) => Promise<TResult>) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (args: TArgs) => run(token!, args),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: posGrantsKey(eventId) })
      // Approving an iPhone creates its Tap to Pay device.
      queryClient.invalidateQueries({ queryKey: ['event-terminals', eventId] })
    },
  })
}

export function useApprovePosGrant(eventId: number) {
  return usePosGrantMutation(eventId, (token, { grantId, trustBooth, vendorEventId }: {
    grantId: number; trustBooth: boolean; vendorEventId: number
  }) => apiClient.approvePosGrant(token, eventId, grantId, trustBooth, vendorEventId))
}

export function useApprovePosGrantByCode(eventId: number) {
  return usePosGrantMutation(eventId, (token, { code, trustBooth }: { code: string; trustBooth: boolean }) =>
    apiClient.approvePosGrantByCode(token, eventId, code, trustBooth))
}

export function useRevokePosGrant(eventId: number) {
  return usePosGrantMutation(eventId, (token, grantId: number) =>
    apiClient.revokePosGrant(token, eventId, grantId))
}

export function useSetPosBoothTrusted(eventId: number) {
  return usePosGrantMutation(eventId, (token, { vendorEventId, trusted }: { vendorEventId: number; trusted: boolean }) =>
    apiClient.setPosBoothTrusted(token, eventId, vendorEventId, trusted))
}

export function useTerminalReload() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, deviceId, userId, numTokens, idempotencyKey }: {
      eventId: number; deviceId: number; userId: number; numTokens: number; idempotencyKey: string
    }) =>
      apiClient.terminalReload(token!, eventId, deviceId, {
        user_id: userId, num_tokens: numTokens, idempotency_key: idempotencyKey,
      }),
    // Same invalidations as useAddCredit — a terminal reload IS an addCredit.
    onSuccess: (_, { eventId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users', eventId] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['userTransactionMetrics', userId, eventId] })
      queryClient.invalidateQueries({ queryKey: ['userEventStats', userId, eventId] })
      queryClient.invalidateQueries({ queryKey: ['userTransactions', userId, eventId] })
    },
  })
}

export function useAddCredit() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ eventId, userId, amount, description }: { eventId: number; userId: number; amount: number; description?: string }) =>
      apiClient.addCredit(token!, userId, eventId, amount, description),
    onSuccess: (_, { eventId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users', eventId] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      // Invalidate user-specific data to update immediately
      queryClient.invalidateQueries({ queryKey: ['userTransactionMetrics', userId, eventId] })
      queryClient.invalidateQueries({ queryKey: ['userEventStats', userId, eventId] })
      // Invalidate user transactions list in modal
      queryClient.invalidateQueries({ queryKey: ['userTransactions', userId, eventId] })
    },
  })
}

export function useTransferTokens() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ eventId, fromUserId, toUserId, amount }: { eventId: number; fromUserId: number; toUserId: number; amount: number }) =>
      apiClient.transferTokens(token!, eventId, fromUserId, toUserId, amount),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['users', eventId] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}

// Content Pages
export function useContentPages(eventId: number, params?: Omit<ContentPagesParams, 'event'>) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.contentPages(eventId, params),
    queryFn: () => apiClient.getContentPages(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useContentPage(pageId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.contentPage(pageId),
    queryFn: () => apiClient.getContentPage(token!, pageId),
    enabled: !!token && !!pageId,
  })
}

export function useCreateContentPage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CreateContentPageData) => apiClient.createContentPage(token!, data),
    onSuccess: (_, { event }) => {
      queryClient.invalidateQueries({ queryKey: ['contentPages', event] })
    },
  })
}

export function useUpdateContentPage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ pageId, data }: { pageId: number; data: Partial<CreateContentPageData> }) =>
      apiClient.updateContentPage(token!, pageId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contentPages'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.contentPage(data.id) })
    },
  })
}

export function useDeleteContentPage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (pageId: number) => apiClient.deleteContentPage(token!, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contentPages'] })
    },
  })
}

// User Modal Data
export function useUserTransactionMetrics(userId: number, eventId: number, tokenValue: number = 2.0) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userTransactionMetrics', userId, eventId, tokenValue] as const,
    queryFn: () => apiClient.getUserTransactionMetrics(token!, userId, eventId, tokenValue),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserEventStats(userId: number, eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userEventStats', userId, eventId] as const,
    queryFn: () => apiClient.getUserEventStats(token!, userId, eventId),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserOrders(userId: number, eventId: number, params?: { page?: number; page_size?: number; limit?: number; offset?: number; search?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userOrders', userId, eventId, params] as const,
    queryFn: () => apiClient.getUserOrders(token!, userId, eventId, params),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserTransactions(userId: number, eventId: number, params?: { page?: number; page_size?: number; limit?: number; offset?: number; search?: string; action?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userTransactions', userId, eventId, params] as const,
    queryFn: () => apiClient.getUserTransactions(token!, userId, eventId, params),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserRedeemableItems(userId: number, eventId: number, params?: { page?: number; page_size?: number; status?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userRedeemableItems', userId, eventId, params] as const,
    queryFn: () => apiClient.getUserRedeemableItems(token!, userId, eventId, params),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserWalletPasses(userId: number, eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['userWalletPasses', userId, eventId] as const,
    queryFn: () => apiClient.getUserWalletPasses(token!, userId, eventId),
    enabled: !!token && !!userId && !!eventId,
  })
}

export function useUserByQrCode() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: ({ qrCode }: { qrCode: string }) =>
      apiClient.getUserByQrCode(token!, qrCode),
  })
}

export function useAddUserWalletPass() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ userId, eventId, barcode }: { userId: number; eventId: number; barcode: string }) =>
      apiClient.addUserWalletPass(token!, userId, eventId, barcode),
    onSuccess: (_, { userId, eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['userWalletPasses', userId, eventId] })
    },
  })
}

export function useDeleteUserWalletPass() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ userId, eventId, barcodeId }: { userId: number; eventId: number; barcodeId: string }) =>
      apiClient.deleteUserWalletPass(token!, userId, eventId, barcodeId),
    onSuccess: (_, { userId, eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['userWalletPasses', userId, eventId] })
    },
  })
}

// Refund Transaction
// Step-up re-authentication: verify the current user's password to open the
// short-lived window required for destructive actions like refunds.
export function useReauth() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: (password: string) => apiClient.reauth(token!, password),
  })
}

export function useRefundTransaction() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ transactionId, amount }: { transactionId: number; eventId: number; amount?: number }) =>
      apiClient.refundTransaction(token!, transactionId, amount),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['transactions', eventId] })
      queryClient.invalidateQueries({ queryKey: ['transaction'] })
    },
  })
}

// Helper: invalidate everything affected by an order refund.
// Order, orders list, financials, dashboard, transactions all change.
// Keys here must use the same first segment as the actual query keys so
// TanStack's prefix-match invalidation fires. The dashboard key is
// `dashboardStats` (not `dashboard`) — easy to get wrong if you grep by
// the human-readable name.
function invalidateOrderRefundQueries(queryClient: ReturnType<typeof useQueryClient>, eventId: number, orderId: number) {
  queryClient.invalidateQueries({ queryKey: ['order', orderId] })
  queryClient.invalidateQueries({ queryKey: ['orders', eventId] })
  queryClient.invalidateQueries({ queryKey: ['financials', eventId] })
  queryClient.invalidateQueries({ queryKey: ['dashboardStats', eventId] })
  queryClient.invalidateQueries({ queryKey: ['transactions', eventId] })
}

// Refund Order — by item IDs (full-line refunds)
export function useRefundOrderItems() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId, itemIds }: { orderId: number; eventId: number; itemIds: number[] }) =>
      apiClient.refundOrderItems(token!, orderId, itemIds),
    onSuccess: (_, { eventId, orderId }) => {
      invalidateOrderRefundQueries(queryClient, eventId, orderId)
    },
  })
}

// Refund Order — partial refund by token amount.
// eventId is forwarded to the API client and sent in the body; backend's
// HasEventPermission requires it for non-staff EventAdmins/OrgAdmins.
export function useRefundOrderPartial() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId, eventId, amount }: { orderId: number; eventId: number; amount: number }) =>
      apiClient.refundOrderPartial(token!, orderId, eventId, amount),
    onSuccess: (_, { eventId, orderId }) => {
      invalidateOrderRefundQueries(queryClient, eventId, orderId)
    },
  })
}

export function useGenerateLoginCode() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: ({ userId, eventId }: { userId: number; eventId: number }) =>
      apiClient.generateLoginCode(token!, userId, eventId),
  })
}

export function useEmailOrderReceipt() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: ({ eventId, orderId }: { eventId: number; orderId: number }) =>
      apiClient.emailOrderReceipt(token!, eventId, orderId),
  })
}

export function useEmailTransactionReceipt() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: ({ eventId, transactionId }: { eventId: number; transactionId: number }) =>
      apiClient.emailTransactionReceipt(token!, eventId, transactionId),
  })
}

// Re-sends an invitee's activation e-mail. Unauthenticated endpoint (the one
// the forgot-password page uses), so no token is needed.
export function useSendActivationCode() {
  return useMutation({
    mutationFn: (email: string) => apiClient.sendActivationCode(email),
  })
}

// Financials
export function useFinancials(eventId: number, params?: { created_at__gte?: string; created_at__lte?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.financials(eventId, params),
    queryFn: () => apiClient.getFinancials(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useSupportReloads(eventId: number, params?: { created_at__gte?: string; created_at__lte?: string; staff?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.supportReloads(eventId, params),
    queryFn: () => apiClient.getSupportReloads(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

// ============================================
// Event Home Page Hooks
// ============================================

export function useEventHomePages(eventId: number, params?: { expand?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.eventHomePages(eventId), params] as const,
    queryFn: () => apiClient.getEventHomePages(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useEventHomePage(homePageId: number, params?: { expand?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.eventHomePage(homePageId), params] as const,
    queryFn: () => apiClient.getEventHomePage(token!, homePageId, params),
    enabled: !!token && !!homePageId,
  })
}

export function useCreateEventHomePage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: { event: number }) => apiClient.createEventHomePage(token!, data),
    onSuccess: (_, { event }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(event) })
    },
  })
}

export function useDeleteEventHomePage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ homePageId }: { homePageId: number; eventId: number }) => 
      apiClient.deleteEventHomePage(token!, homePageId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

// ============================================
// Event Navigation Hooks
// ============================================

export function useEventCategories(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.eventCategories(eventId),
    queryFn: () => apiClient.getEventCategories(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useCreateEventCategory() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, name }: { eventId: number; name: string }) =>
      apiClient.createEventCategory(token!, { event: eventId, name }),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventCategories(eventId) })
    },
  })
}

export function useUpdateEventCategory() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId, data }: {
      eventId: number
      categoryId: number
      data: Partial<{ name: string; order: number }>
    }) => apiClient.updateEventCategory(token!, categoryId, data),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventCategories(eventId) })
    },
  })
}

export function useDeleteEventCategory() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ categoryId }: { eventId: number; categoryId: number }) =>
      apiClient.deleteEventCategory(token!, categoryId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventCategories(eventId) })
      // Assignments changed too (SET_NULL / through-row cascade on the backend)
      queryClient.invalidateQueries({ queryKey: queryKeys.vendorEvents(eventId) })
    },
  })
}

export function useEventNavigations(eventId: number, params?: {
  is_published?: boolean
  search?: string
  ordering?: string
  expand?: string
}) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.eventNavigations(eventId), params] as const,
    queryFn: () => apiClient.getEventNavigations(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useEventNavigation(navigationId: number, params?: { expand?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: [...queryKeys.eventNavigation(navigationId), params] as const,
    queryFn: () => apiClient.getEventNavigation(token!, navigationId, params),
    enabled: !!token && !!navigationId,
  })
}

export function useCreateEventNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: Parameters<typeof apiClient.createEventNavigation>[1]) => 
      apiClient.createEventNavigation(token!, data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigations(data.event) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(data.event) })
    },
  })
}

export function useUpdateEventNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ navigationId, eventId, data }: { 
      navigationId: number
      eventId: number
      data: Parameters<typeof apiClient.updateEventNavigation>[2] 
    }) => apiClient.updateEventNavigation(token!, navigationId, { event: eventId, ...data }),
    onSuccess: (_, { eventId, navigationId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigations(eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigation(navigationId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

export function useDeleteEventNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ navigationId }: { navigationId: number; eventId: number }) => 
      apiClient.deleteEventNavigation(token!, navigationId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigations(eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

export function useUploadNavigationImage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ navigationId, eventId, file }: { navigationId: number; eventId: number; file: File }) => 
      apiClient.uploadNavigationImage(token!, navigationId, eventId, file),
    onSuccess: (_, { eventId, navigationId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigations(eventId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventNavigation(navigationId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

// ============================================
// Event Home Page Navigation (Grid Layout) Hooks
// ============================================

export function useEventHomePageNavigations(homePageId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.eventHomePageNavigations(homePageId),
    queryFn: () => apiClient.getEventHomePageNavigations(token!, homePageId),
    enabled: !!token && !!homePageId,
  })
}

export function useCreateEventHomePageNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: Parameters<typeof apiClient.createEventHomePageNavigation>[1] & { eventId?: number }) => 
      apiClient.createEventHomePageNavigation(token!, data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePageNavigations(data.home_page) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePage(data.home_page) })
      if (data.eventId) queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(data.eventId) })
    },
  })
}

export function useUpdateEventHomePageNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { 
      id: number
      homePageId: number
      eventId?: number
      data: Parameters<typeof apiClient.updateEventHomePageNavigation>[2] 
    }) => apiClient.updateEventHomePageNavigation(token!, id, data),
    onSuccess: (_, { homePageId, eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePageNavigations(homePageId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePage(homePageId) })
      if (eventId) queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

export function useDeleteEventHomePageNavigation() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id }: { id: number; homePageId: number; eventId?: number }) => 
      apiClient.deleteEventHomePageNavigation(token!, id),
    onSuccess: (_, { homePageId, eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePageNavigations(homePageId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePage(homePageId) })
      if (eventId) queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

export function useBulkUpdateHomePageNavigationPositions() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ updates }: { 
      homePageId: number
      eventId?: number
      updates: Parameters<typeof apiClient.bulkUpdateHomePageNavigationPositions>[1] 
    }) => apiClient.bulkUpdateHomePageNavigationPositions(token!, updates),
    onSuccess: (_, { homePageId, eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePageNavigations(homePageId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePage(homePageId) })
      if (eventId) queryClient.invalidateQueries({ queryKey: queryKeys.eventHomePages(eventId) })
    },
  })
}

// Site Map
export function useMapConfig(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.mapConfig(eventId),
    queryFn: () => apiClient.getMapConfig(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useUpdateMapConfig() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: number; data: Partial<import('./types').MapConfig> | FormData }) =>
      apiClient.updateMapConfig(token!, eventId, data),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mapConfig(eventId) })
    },
  })
}

export function useMapHeatmap(eventId: number, params?: {
  start_date?: string
  end_date?: string
  metric?: 'transactions' | 'revenue'
}, options?: { enabled?: boolean }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.mapHeatmap(eventId, params as Record<string, unknown>),
    queryFn: () => apiClient.getMapHeatmap(token!, eventId, params),
    enabled: !!token && !!eventId && (options?.enabled !== false),
  })
}

export function useMapTimeline(eventId: number, params?: {
  start_date?: string
  end_date?: string
  interval?: 'minute' | 'hour'
  metric?: 'transactions' | 'revenue'
  user_id?: number
  user_ids?: string
  item_id?: number
}, options?: { enabled?: boolean }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.mapTimeline(eventId, params as Record<string, unknown>),
    queryFn: () => apiClient.getMapTimeline(token!, eventId, params),
    enabled: !!token && !!eventId && (options?.enabled !== false),
  })
}

// Live Stats
export function useLiveStats(eventId: number, activeWindow = 15, enabled = true) {
  const { token } = useAuth()
  const serverTimeRef = useRef<string | null>(null)

  const query = useQuery({
    queryKey: queryKeys.liveStats(eventId, activeWindow),
    queryFn: () =>
      apiClient.getLiveStats(token!, eventId, {
        since: serverTimeRef.current ?? undefined,
        active_window: activeWindow,
      }),
    enabled: !!token && !!eventId && enabled,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

  useEffect(() => {
    if (query.data?.server_time) {
      serverTimeRef.current = query.data.server_time
    }
  }, [query.data?.server_time])

  return query
}

export function useLiveTransactions(eventId: number, activeWindow = 15) {
  const [allTransactions, setAllTransactions] = useState<
    LiveStatsResponse['transactions']
  >([])

  const { data } = useLiveStats(eventId, activeWindow)

  useEffect(() => {
    if (data?.transactions?.length) {
      setAllTransactions((prev) => {
        const existingIds = new Set(prev.map((t) => t.order_id))
        const newOnes = data.transactions.filter(
          (t) => !existingIds.has(t.order_id)
        )
        if (newOnes.length === 0) return prev
        return [...newOnes, ...prev].slice(0, 200)
      })
    }
  }, [data?.transactions])

  return allTransactions
}

export function useLiveHeatmap(eventId: number, activeWindow = 15) {
  const [vendorMap, setVendorMap] = useState<
    Map<number, LiveStatsResponse['vendors'][0]>
  >(new Map())

  const { data } = useLiveStats(eventId, activeWindow)

  useEffect(() => {
    if (data?.vendors?.length) {
      setVendorMap((prev) => {
        const next = new Map(prev)
        for (const v of data.vendors) {
          const existing = next.get(v.vendor_event_id)
          if (existing) {
            next.set(v.vendor_event_id, {
              ...v,
              tokens_spent: existing.tokens_spent + v.tokens_spent,
              dollars_spent: existing.dollars_spent + v.dollars_spent,
              num_items: existing.num_items + v.num_items,
              num_orders: existing.num_orders + v.num_orders,
            })
          } else {
            next.set(v.vendor_event_id, v)
          }
        }
        return next
      })
    }
  }, [data?.vendors])

  return Array.from(vendorMap.values())
}

export function useLiveLeaderboard(eventId: number, activeWindow = 15) {
  type LeaderboardItem = LiveStatsResponse['product_leaderboard'][0]
  const [productMap, setProductMap] = useState<Map<string, LeaderboardItem>>(new Map())
  const isFirstPollRef = useRef(true)

  const { data } = useLiveStats(eventId, activeWindow)

  useEffect(() => {
    if (!data?.product_leaderboard?.length) return

    setProductMap((prev) => {
      // On the first poll (no ?since), replace entirely — this is the full window snapshot
      if (isFirstPollRef.current) {
        isFirstPollRef.current = false
        const next = new Map<string, LeaderboardItem>()
        for (const p of data.product_leaderboard) {
          const key = `${p.item_name}::${p.vendor_name}`
          next.set(key, p)
        }
        return next
      }

      // On subsequent polls (delta), merge by accumulating quantities
      const next = new Map(prev)
      for (const p of data.product_leaderboard) {
        const key = `${p.item_name}::${p.vendor_name}`
        const existing = next.get(key)
        if (existing) {
          next.set(key, {
            ...p,
            qty_sold: existing.qty_sold + p.qty_sold,
            tokens_revenue: existing.tokens_revenue + p.tokens_revenue,
            dollars_revenue: existing.dollars_revenue + p.dollars_revenue,
          })
        } else {
          next.set(key, p)
        }
      }
      return next
    })
  }, [data?.product_leaderboard])

  // Return sorted by qty_sold descending
  return Array.from(productMap.values()).sort((a, b) => b.qty_sold - a.qty_sold)
}

// Reviews

export function useOrderReviews(params?: Record<string, string | number | boolean | undefined | null>) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.orderReviews(params),
    queryFn: () => apiClient.getOrderReviews(token!, params),
    enabled: !!token,
  })
}

export function useOrderReview(id: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.orderReview(id),
    queryFn: () => apiClient.getOrderReview(token!, id),
    enabled: !!token && !!id,
  })
}

export function useOrderReviewsByOrder(orderId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.orderReviews({ order: orderId }),
    queryFn: () => apiClient.getOrderReviews(token!, { order: orderId, expand: 'user' }),
    enabled: !!token && !!orderId,
  })
}

export function useOrderReviewSummary(params?: Record<string, string | number | boolean | undefined | null>) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.orderReviewSummary(params),
    queryFn: () => apiClient.getOrderReviewSummary(token!, params),
    enabled: !!token,
  })
}

export function useItemReviews(params?: Record<string, string | number | boolean | undefined | null>) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.itemReviews(params),
    queryFn: () => apiClient.getItemReviews(token!, params),
    enabled: !!token,
  })
}

export function useItemReviewSummary(params?: Record<string, string | number | boolean | undefined | null>) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.itemReviewSummary(params),
    queryFn: () => apiClient.getItemReviewSummary(token!, params),
    enabled: !!token,
  })
}

// Support Chat — history of the current user's past sessions for an event.
// Used by the drawer's History panel. staleTime: 0 + refetchOnMount so the
// operator always sees a fresh list when reopening the panel (resumed sessions
// can change state quickly).
export function useSupportChatSessions(eventId: number, enabled: boolean = true) {
  const { token } = useAuth()
  return useQuery({
    queryKey: queryKeys.supportChatSessions(eventId),
    queryFn: () => apiClient.listSupportChatSessions(token!, eventId),
    enabled: !!token && !!eventId && enabled,
    staleTime: 0,
  })
}
